import bind from 'lodash/bind';
import isNull from 'lodash/isNull';
import isString from 'lodash/isString';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classNames from 'classnames';
import documentOffset from 'document-offset';
import DOMUtils from '@spothero/utils/dom';
import Button from 'button-component';
import RenderInBody from 'render-in-body-component';
import Sentry from 'sentry';

class Tooltip extends Component {
    constructor(props) {
        super(props);

        this._isArrowAdjusted = false;
        this._closeClick = this._closeClick.bind(this);

        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentDidMount() {
        const {
            triggerItem,
            updateOnMove,
            closeOnBodyClick
        } = this.props;
        const {
            container
        } = this.refs;

        // have to check against null here as a React component can have a null state if it doesn't define one
        // whereas a DOM element will have a state of undefined
        this._trigger = (isNull(triggerItem.state) || triggerItem.state)
            ? ReactDOM.findDOMNode(triggerItem)
            : (isString(triggerItem))
                ? DOMUtils.el(triggerItem)
                : triggerItem;

        Sentry.addData({
            triggerItem,
            _trigger: this._trigger,
            triggerBoundingRect: (this._trigger) ? this._trigger.getBoundingClientRect() : null,
            docOffsetData: (this._trigger) ? documentOffset(this._trigger) : null,
            allProps: this.props
        });

        this._positionTooltip();

        DOMUtils.addClass(container, 'Tooltip-showing');

        if (updateOnMove) {
            container.style.position = 'fixed';

            this._scrollInterval = setInterval(bind(this._positionTooltip, this), 100);
        }

        if (closeOnBodyClick) {
            document.addEventListener('click', this._closeClick);
        }
    }

    componentWillUnmount() {
        const {
            updateOnMove,
            closeOnBodyClick
        } = this.props;

        if (updateOnMove) {
            clearInterval(this._scrollInterval);
        }

        if (closeOnBodyClick) {
            document.removeEventListener('click', this._closeClick);
        }
    }

    _positionTooltip() {
        if (!this._trigger) { return; }

        const {
            position,
            tipOffset,
            updateOnMove,
            adjustArrow
        } = this.props;
        const {
            container,
            arrow
        } = this.refs;
        const {
            top: clientTop,
            left: clientLeft,
            right: clientRight,
            bottom: clientBottom,
            width,
            height
        } = this._trigger.getBoundingClientRect();
        const {
            top: documentTop,
            left: documentLeft
        } = documentOffset(this._trigger) || {top: 100, left: 100};
        const top = (updateOnMove) ? clientTop : documentTop;
        const right = (updateOnMove) ? clientRight : (documentLeft + width);
        const bottom = (updateOnMove) ? clientBottom : (documentTop + height);
        const left = (updateOnMove) ? clientLeft : documentLeft;
        const containerMid = (position === 'top' || position === 'bottom')
            ? (container.offsetWidth / 2)
            : (container.offsetHeight / 2);
        const bodyWidth = document.body.clientWidth;
        const adjuster = 5;
        let newTop = 0;
        let newLeft = 0;

        switch (position) {
            case 'top':
                newTop = top - container.offsetHeight - tipOffset;
                newLeft = (left + (width / 2)) - containerMid;
                break;

            case 'right':
                newTop = top + (height / 2) - containerMid;
                newLeft = right + tipOffset;
                break;

            case 'bottom':
                newTop = bottom + tipOffset;
                newLeft = (left + (width / 2)) - containerMid;
                break;

            case 'left':
                newTop = (top + (height / 2)) - containerMid;
                newLeft = left - container.offsetWidth - tipOffset;
                break;
        }

        // adjust tooltip position if off the left side of the screen
        let adjustedLeft = (newLeft < 0) ? adjuster : newLeft;

        // adjust tooltip position if off the right side of the screen
        adjustedLeft = (width > (bodyWidth - adjustedLeft)) ? (bodyWidth - width - adjuster) : adjustedLeft;

        container.style.top = `${newTop}px`;
        container.style.left = `${adjustedLeft}px`;

        if (adjustArrow && !this._isArrowAdjusted && adjustedLeft !== newLeft && (position === 'top' || position === 'bottom')) {
            arrow.style.left = `${arrow.getBoundingClientRect().left + (newLeft - adjustedLeft)}px`;

            this._isArrowAdjusted = true;
        }
    }

    _closeClick(evt) {
        if (DOMUtils.hasClass(this.refs.container, 'Tooltip-showing')) {
            DOMUtils.removeClass(this.refs.container, 'Tooltip-showing');

            setTimeout(() => {
                this.props.onHidden();
            }, 250);
        }
    }

    _renderTooltip() {
        const {
            className,
            position,
            showClose,
            children
        } = this.props;
        const classes = classNames(
            'Tooltip',
            `Tooltip-${position}`,
            {'Tooltip-no-close': !showClose},
            className
        );

        return (
            <div className={classes} role="tooltip" ref="container">
                <div className="Tooltip-arrow" ref="arrow" />
                <div className="Tooltip-content">
                    {children}
                    {showClose &&
                        <Button
                            className="Tooltip-close"
                            icon="times"
                            onClick={this._closeClick}
                        />
                    }
                </div>
            </div>
        );
    }

    render() {
        return <RenderInBody>{this._renderTooltip()}</RenderInBody>;
    }
}

Tooltip.propTypes = {
    className: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.string
    ]).isRequired,
    position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    showClose: PropTypes.bool,
    /** The element to attach the tooltip to. It can be a className of an element, a React component, or the element itself. */
    triggerItem: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string
    ]).isRequired,
    /** Whether to reposition the tooltip when it moves, be it via window resize or scrolling or whatever moves it. */
    updateOnMove: PropTypes.bool,
    onHidden: PropTypes.func,
    tipOffset: PropTypes.number,
    adjustArrow: PropTypes.bool,
    closeOnBodyClick: PropTypes.bool
};

Tooltip.defaultProps = {
    className: '',
    position: 'top',
    showClose: true,
    updateOnMove: false,
    tipOffset: 0,
    adjustArrow: true
};

export default Tooltip;



// WEBPACK FOOTER //
// ./frontend/src/js/components/tooltip/Tooltip.jsx